/*!

=========================================================
* Argon Dashboard PRO React - v1.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2020 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
// react component for creating dynamic tables
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
// reactstrap components
import {
  Card,
  CardHeader,
  Container,
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "reactstrap";
// core components
import SimpleHeader from "components/Headers/SimpleHeader.js";
import BuyerForm from "components/Forms/BuyerForm"
import { UserContext } from "lib/UserContext";
import axiosInstance from "api";
import moment from "moment";

const pagination = paginationFactory({
  page: 1,
  alwaysShowAllBtns: true,
  sizePerPage: 50,
  showTotal: true,
  withFirstAndLast: false,
  sizePerPageRenderer: ({ options, currSizePerPage, onSizePerPageChange }) => (
    <div className="dataTables_length" id="datatable-basic_length">
      <label>
        Show{" "}
        {
          <select
            name="datatable-basic_length"
            aria-controls="datatable-basic"
            className="form-control form-control-sm"
            onChange={e => onSizePerPageChange(e.target.value)}
          >
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        }{" "}
        entries.
      </label>
    </div>
  )
});

const { SearchBar } = Search;

class Buyers extends React.Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    const rowEvents = {
      onClick: (e, row, rowIndex) => {
        delete row.registration.id; // Prevents spread opperator from overriding the id of the buyer
        this.setState({
          selectedItem: {...row, ...row.registration},
          editAppointments: false,
          selectedBuyer: {...row},
        })
      }
    };

    this.state = {
      rowEvents,
      selectedItem: null,
      error: null,
      confirmEmails: false,
      selectedBuyer: null,
    };

    this.onError = this.onError.bind(this);
    this.onReset = this.onReset.bind(this);
    this.toggleEmailModal = this.toggleEmailModal.bind(this);
    this.toggleDeletePhotoModal = this.toggleDeletePhotoModal.bind(this);
    this.deletePhoto = this.deletePhoto.bind(this);
    this.toggleDeleteRegistrationModal = this.toggleDeleteRegistrationModal.bind(this);
    this.deleteRegistrant = this.deleteRegistrant.bind(this);
    this.archiveRegistrant = this.archiveRegistrant.bind(this);
    this.downloadSchedule = this.downloadSchedule.bind(this);
  }

  toggleDeleteRegistrationModal = () => {
    const isOpen = this.state.deleteRegistration;
    this.setState({deleteRegistration: !isOpen});
  }

  toggleDeletePhotoModal = () => {
    const isOpen = this.state.deletePhoto;
    this.setState({deletePhoto: !isOpen});
  } 

  toggleEmailModal = () => {
    const isOpen = this.state.confirmEmails;
    this.setState({confirmEmails: !isOpen})
  }

  printFinalConfirmations = async () => {
    this.context.setState(s => { return { ...s, loading: true }});
    const response = await axiosInstance.post(`/registration/bulk-print/buyer/${this.context.activeEvent.id}`, {}, {responseType: 'blob'});

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.style.display = "none";
    link.href = url;
    link.setAttribute('download', "BuyerFinalConfirmations.pdf");
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(link.href);
    document.body.removeChild(link);

    this.context.setState(s => { return { ...s, loading: false }});
  }

  deleteRegistrant = async () => {
    try {
      const selectedItem = this.state.selectedItem;
      await axiosInstance.delete(
        `registration/delete-buyer/${selectedItem.personId}/${selectedItem.id}/${selectedItem.registrationId}/ ${this.context.activeEvent.id}`);
      this.onReset();
    } catch (error) {
      this.onError(error.response?.data || "An error occured");
    }
  }

  archiveRegistrant = async () => {
    try {
      await axiosInstance.post(`registration/toggle-archive-buyer`, {
        eventId: this.context.activeEvent.id,
        registration: this.state.selectedBuyer.registration,
        buyerRegistration: this.state.selectedBuyer,
      });
      this.onReset();
    } catch (error) {
      console.error(error)
      this.onError(error.response?.data || "An error occured");
    }
  }

  deletePhoto = async () => {
    this.context.setState(s => { return { ...s, loading: true }});
    const selectedItem = this.state.selectedItem;
    const selectedBuyer = this.context.buyers.find(f => f.registrationId === selectedItem.registrationId);
    this.setState({selectedItem: null});
    if (selectedItem.registrationId && selectedBuyer) {
      await axiosInstance.post(`registration/buyer/delete-photo/${selectedItem.registrationId}`);
      this.setState({selectedItem: {...selectedItem, imageString: ""}});
      this.context.setState(s => ({...s, buyers: s.buyers.filter(f => f.id !== selectedBuyer.id).concat([{...selectedBuyer, registration: {...selectedBuyer, imageString: ""}}])}));
    }
    this.context.setState(s => ({ ...s, loading: false }));
  }

  sendBulkEmails = async () => {
    try {
      this.context.setState(s => { return { ...s, loading: true }});
      await axiosInstance.post(`/registration/bulk-send/buyer/${this.context.activeEvent.id}`);
    } catch (error) {
      this.onError("Something went wrong. Is the final confirmation template uploaded?")
    } finally {
      this.context.setState(s => { return { ...s, loading: false }});
    }
  }

  sendEmail = async () => {
    try {
      this.context.setState(s => { return { ...s, loading: true }});
      await axiosInstance.post(`/registration/send-email/buyer/${this.state.selectedItem.registrationId}`);
    } catch (error) {
      this.onError("Something went wrong. Is the final confirmation template uploaded?")
    } finally {
      this.context.setState(s => { return { ...s, loading: false }});
    }
  }

  downloadSchedule = async () => {
    try {
      this.context.setState(s => { return { ...s, loading: true }});
      const response = await axiosInstance.get(
        `/registration/download-buyer-schedule/${this.state.selectedItem.id}/${this.context.activeEvent.id}`,
        {
          responseType: "blob"
        });
      const {data} = response;
      const blob = new Blob([data], {type: "application/pdf"})
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'FISA_EventAppointmentSchedule.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      this.onError(error.response?.data ?? "An error happened")
    } finally {
      this.context.setState(s => { return { ...s, loading: false }});
    }
  }

  exportTravelInfo = () => {
    let text = `Booking Flights ${moment(new Date()).format("MM/DD/YYYY")}\n\n`;
    for (const buyer of this.context.buyers) {
      text += `First and Last Name: ${buyer.registration.firstName || "Not Specified"} ${buyer.registration.lastName || "Not Specified"}`
            + `\nDriver’s License Name: ${buyer.boardingPassName || "Not Specified"}`
            + `\nBirthdate: ${moment(buyer.registration.birthDate).format("MM/DD/YYYY") || "Not Specified"}`
            + `\nEmail address: ${buyer.registration.email || "Not Specified"}`
            + `\nCell #: ${buyer.registration.cellphone || "Not Specified"}`
            + `\nCity, State and Zip Code: ${buyer.city || "Not Specified"}, ${buyer.state || "Not Specified"} ${buyer.zip || "Not Specified"}`
            + `\nDate of Departure: ${moment(buyer.flightDepartureDate).format("MM/DD/YYYY") || "Not Specified"}`
            + `\nDate of Return: ${moment(buyer.flightArrivalDate).format("MM/DD/YYYY") || "Not Specified"}`
            + `\nPreferred Departure City: ${buyer.departureCity || "Not Specified"}`
            + `\nAlternate Departure City: ${buyer.alternateDepartureCity || "Not Specified"}`
            + `\nFrequent Flier Airline (1): ${buyer.frequentFlyerAirline1 || "Not Specified"}`
            + `\nFrequent Flier Acct # (1): ${buyer.frequentFlyerNumber1 || "Not Specified"}`
            + `\nFrequent Flier Airline (2): ${buyer.frequentFlyerAirline2 || "Not Specified"}`
            + `\nFrequent Flier Acct # (2): ${buyer.frequentFlyerNumber2 || "Not Specified"}`
            + "\n\n-----------------------------------------------------------------------------\n\n";
    }

    const blob = new Blob([text], { type: "text/plain" });

    const a = document.createElement('a');
    a.download = `${moment(new Date()).format("MM/DD/YYYY")}_TravelInfor.txt`;
    a.href = URL.createObjectURL(blob);
    a.dataset.downloadurl = ["text/plain", a.download, a.href].join(':');
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(function() { URL.revokeObjectURL(a.href); }, 1500);
  }

  onReset = () => {
    this.setState({ error: null, selectedItem: null })
    const activeEvent = this.context.activeEvent;
    this.context.setState(s => ({...s, activeEvent: null}))
    this.context.selectEvent(activeEvent);
  }

  onError = (err) => {
    this.setState({ error: err })
  }

  render() {
    return (
      <>
        <SimpleHeader name="Buyers" parentName="Home" />
        {this.context.activeEvent && <Container className="mt--6" fluid>
          <Row>
            <div className="col">
              {this.state.selectedItem === null &&
              <Card>
                <CardHeader>
                  <h3 className="mb-0">Buyers</h3>
                </CardHeader>
                <ToolkitProvider
                  data={this.context.activeEvent ? this.context.buyers : []}
                  keyField="id"
                  columns={[
                    {
                      headerStyle: { color: "black" },
                      dataField: "registration.firstName",
                      text: "First Name",
                      sort: true
                    },
                    {
                      headerStyle: { color: "black" },
                      dataField: "registration.lastName",
                      text: "Last Name",
                      sort: true
                    },
                    {
                      headerStyle: { color: "black" },
                      dataField: "registration.title",
                      text: "Job Title",
                      sort: true
                    },
                    {
                      headerStyle: { color: "black" },
                      dataField: "registration.organizationName",
                      text: "Company/Organization",
                      sort: true
                    },
                    {
                      headerStyle: { color: "black" },
                      dataField: "registration.email",
                      text: "Email",
                      sort: true
                    },
                    {
                      headerStyle: { color: "black" },
                      dataField: "registration.webUrl",
                      text: "Website",
                      sort: true
                    },
                    {
                      headerStyle: { color: "black" },
                      dataField: "invitedBy",
                      text: "Referred By",
                      sort: true
                    },
                    {
                      headerStyle: { color: "black" },
                      dataField: "archived",
                      text: "Canceled",
                    }
                  ]}
                  search
                >
                  {props => (
                    <div className="py-4 table-responsive">
                      <div
                        id="datatable-basic_filter"
                        className="dataTables_filter w-100 px-4 pb-1"
                      >
                        <label>
                          Search:
                          <SearchBar
                            className="form-control-sm"
                            placeholder=""
                            {...props.searchProps}
                          />
                        </label>
                        <Button color="dark" className="float-right btn-sm mb-2" onClick={() => this.setState({selectedItem: {}})} disabled={!this.context.activeEvent}>Create Buyer</Button>
                        <Button color="dark" className="float-right btn-sm mb-2" onClick={this.exportTravelInfo} disabled={!this.context.activeEvent}>Export Travel Info</Button>
                        <Button color="dark" className="float-right btn-sm mr-2 mb-2" onClick={this.printFinalConfirmations}>Print Final Confirmations</Button>
                        {this.context.activeEvent && <Button color="dark" className="float-right btn-sm mr-2 mb-2" onClick={this.toggleEmailModal} disabled={!this.context.activeEvent.buyerFinalConfirmationEmailTemplate}>Send Bulk Email Confirmations</Button>}
                      </div>
                      <BootstrapTable
                        {...props.baseProps}
                        bootstrap4={true}
                        pagination={pagination}
                        bordered={false}
                        rowEvents={this.state.rowEvents}
                      />
                    </div>
                  )}
                </ToolkitProvider>
              </Card>}
              {this.state.selectedItem !== null && 
              <Card>
                <CardHeader>
                  <Button close onClick={() => this.setState({selectedItem: null})}/>
                  <Row>
                    <Col>
                      {this.state.selectedItem.id && <>
                        <h3 className="mb-0">{this.state.selectedItem.firstName} {this.state.selectedItem.lastName}, {this.state.selectedItem.organizationName} </h3>
                      </>}
                      {!this.state.selectedItem.id && <>
                        <h3 className="mb-0">Create New Buyer </h3>
                      </>}
                    </Col>
                    {this.state.selectedItem.id && this.context.activeEvent && <Col lg="auto">
                      <Button color="dark" outline onClick={this.sendEmail} disabled={!this.context.activeEvent.buyerFinalConfirmationEmailTemplate}>Send Final Confirmation</Button>
                      <Button color="dark" onClick={this.downloadSchedule}>Download Schedule</Button>
                      <Button color="danger" onClick={this.toggleDeleteRegistrationModal}>Delete Buyer</Button>
                      <Button color={!this.state.selectedItem.archived ? "danger" : "success"} onClick={this.archiveRegistrant}>{!this.state.selectedItem.archived ? "Cancel Registration" : "Resume Registration"}</Button>
                    </Col>}
                  </Row>
                </CardHeader>
                <BuyerForm
                  data={{
                    ...this.state.selectedItem,
                    timeZone: this.context.activeEvent.timeZone
                  }}
                  eventId={this.context.activeEvent.id}
                  ymca={this.context.activeEvent.ymca}
                  onError={this.onError}
                  onSuccess={this.onReset}
                  deletePhoto={this.toggleDeletePhotoModal}
                />
              </Card>}
            </div>
          </Row>
          <Modal isOpen={this.state.deleteRegistration}>
            <ModalHeader>Delete Buyer?</ModalHeader>
            <ModalBody>
              Are you sure that you want to delete this buyer?
            </ModalBody>
            <ModalFooter>
              <Button color="primary" onClick={() => {this.deleteRegistrant(); this.toggleDeleteRegistrationModal()}}>Yes</Button>
              <Button color="secondary" onClick={this.toggleDeleteRegistrationModal}>Cancel</Button>
            </ModalFooter>
          </Modal>
          <Modal isOpen={this.state.deletePhoto}>
            <ModalHeader>Delete Photo?</ModalHeader>
            <ModalBody>
              Are you sure that you want to delete this photo?
            </ModalBody>
            <ModalFooter>
              <Button color="primary" onClick={() => {this.deletePhoto(); this.toggleDeletePhotoModal()}}>Yes</Button>
              <Button color="secondary" onClick={this.toggleDeletePhotoModal}>Cancel</Button>
            </ModalFooter>
          </Modal>
          <Modal isOpen={this.state.confirmEmails} toggle={this.toggleEmailModal}>
            <ModalHeader toggle={this.toggleEmailModal}>Send Bulk Final Confirmation Emails?</ModalHeader>
            <ModalBody>
              Are you sure that you want to send bulk final confirmation emails?
            </ModalBody>
            <ModalFooter>
              <Button color="primary" onClick={() => {this.sendBulkEmails(); this.toggleEmailModal()}}>Yes</Button>
              <Button color="secondary" onClick={this.toggleEmailModal}>Cancel</Button>
            </ModalFooter>
          </Modal>
          <Modal isOpen={!!this.state.error}>
            <ModalHeader toggle={() => { this.setState({error: null}) }}>Error</ModalHeader>
            <ModalBody>
              {this.state.error}
            </ModalBody>
          </Modal>
        </Container>}
        {!this.context.activeEvent && <Container className="mt--6" fluid>
          <Row>
            <div className="col">
              <Card>
                <CardHeader>
                  No Event Selected
                </CardHeader>
              </Card>
            </div>
          </Row>
        </Container>}
      </>
    );
  }
}

export default Buyers;
